import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { t } from "i18next";
import {
  gxPaymentsBulkReviewTransactionViewSummaryApi,
  gxPaymentsBulkReviewTransactionByStatusApi,
  gxPayrollReviewTransactionViewSummaryApi,
  gxPayrollBulkTransactionByStatusApi,
} from "@/app/apiConfig/services/bulkPaymentsPayrollApiLayer";
import type {
  PaymentsBulkReviewTransViewSummaryResponse,
  PaymentsBulkFileTransaction,
  PayrollReviewTransactionViewSummaryResponse,
  GxwpsBulkFileTransaction,
  PaymentsBulkApprovalViewSummaryResponse,
} from "@/app/types/bulkPaymentsPayroll";
import { Loader } from "@progress/kendo-react-indicators";
import BulkPaymentReviewOverview from "../BulkPaymentReview/BulkPaymentReviewOverview";
import BulkPaymentReviewCardList from "../BulkPaymentReview/BulkPaymentReviewCardList";
import {
  STATUS_CODES,
  type BulkUploadIndividualDetailsProps,
} from "../BulkUpload";
import BulkUploadsMetroMap from "../BulkPaymentReview/BulkUploadsMetroMap";
import "./styles/BulkUploadApprove.scss";

const BulkUploadIndividualDetails = ({
  bulkData: propBulkData,
  bulkType: propBulkType,
  isApprovalMode = false,
  onSummaryLoaded, 
  // onSummaryLoaded, // NEW ==========
}: BulkUploadIndividualDetailsProps) => {
  const location = useLocation();

  const transferredType = propBulkType || location.state?.bulkType || "PAYMENT";
  const transferredData = propBulkData || location.state?.bulkData;
  const section = location.state?.section;
  const gxJobProcessid = transferredData?.gxJobProcessid;

  const fileFormat = String(
    (
      transferredData as PaymentsBulkApprovalViewSummaryResponse & {
        fileFormat?: string;
      }
    )?.fileFormat || "",
  )
    .toUpperCase()
    .trim();

  const isPayrollFlow = transferredType === "PAYROLL" || section === "payrolls";
  const [hideTabs, setHideTabs] = useState<boolean>(false);
  const [cardListTabLoading, setCardListTabLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [reviewSummary, setReviewSummary] =
    useState<PaymentsBulkReviewTransViewSummaryResponse | null>(null);
  const [allTransactions, setAllTransactions] = useState<
    PaymentsBulkFileTransaction[]
  >([]);
  const [validTransactions, setValidTransactions] = useState<
    PaymentsBulkFileTransaction[]
  >([]);
  const [invalidTransactions, setInvalidTransactions] = useState<
    PaymentsBulkFileTransaction[]
  >([]);
  const [pendingTransactions, setPendingTransactions] = useState<
    PaymentsBulkFileTransaction[]
  >([]);
  const [payrollReviewSummary, setPayrollReviewSummary] =
    useState<PayrollReviewTransactionViewSummaryResponse | null>(null);
  const [allPayrollTransactions, setAllPayrollTransactions] = useState<
    GxwpsBulkFileTransaction[]
  >([]);
  const [validPayrollTransactions, setValidPayrollTransactions] = useState<
    GxwpsBulkFileTransaction[]
  >([]);
  const [invalidPayrollTransactions, setInvalidPayrollTransactions] = useState<
    GxwpsBulkFileTransaction[]
  >([]);
  const [logoName, setLogoName] = useState<string>("");
  const [logoPath, setLogoPath] = useState<string>("");
  const [bankName, setBankName] = useState<string>("");
  const [debitAccountNo, setDebitAccountNo] = useState<string>("");
  const hasFetchedRef = useRef(false);

  const getCurrentStatusCode = (): string => {
    if (isPayrollFlow && payrollReviewSummary) {
      return String(payrollReviewSummary.jobStatusCode || "");
    }
    if (!isPayrollFlow && reviewSummary) {
      return String(reviewSummary.jobStatusCode || "");
    }
    return "";
  };

  const getJobStatusDesc = (): string => {
    if (isPayrollFlow && payrollReviewSummary) {
      return payrollReviewSummary.jobStatusDesc ?? "";
    }
    if (!isPayrollFlow && reviewSummary) {
      return reviewSummary.jobStatusDesc ?? "";
    }
    return "";
  };

  const statusCode = getCurrentStatusCode();
  const jobStatusDesc = getJobStatusDesc();

  const isReadyForReview =
    statusCode === STATUS_CODES.READY_FOR_REVIEW_PAYMENT ||
    statusCode === STATUS_CODES.READY_FOR_REVIEW_PAYROLL;

  const isPendingApproval = statusCode === STATUS_CODES.AWAITING_APPROVAL;
  const isInProcess = statusCode === STATUS_CODES.IN_PROCESS;
  const isProcessed = statusCode === STATUS_CODES.PROCESSED;
  const isRejected = statusCode === STATUS_CODES.REJECTED;

  const shouldShowTabs = hideTabs
    ? false
    : isReadyForReview || isRejected || (isApprovalMode && isPendingApproval);

  const shouldShowTimeline =
    !isApprovalMode && (isPendingApproval || isInProcess || isProcessed);

  const shouldShowTransactionSummary = isInProcess || isProcessed;

  // === PAYMENT DATA FETCH ===================================================
  const fetchPaymentData = async () => {
    const summaryResponse = await gxPaymentsBulkReviewTransactionViewSummaryApi(
      {
        gxJobProcessId: gxJobProcessid,
      },
    );

    if (summaryResponse.status === 1 && summaryResponse.data) {
      setReviewSummary(summaryResponse.data);
      setAllTransactions(summaryResponse.data.gxPayBulkFileTransaction || []);

      if (summaryResponse.data.logoName) {
        setLogoName(summaryResponse.data.logoName);
      }
      if (summaryResponse.data.logoPath) {
        setLogoPath(summaryResponse.data.logoPath);
      }
      if (summaryResponse.data.bankName) {
        setBankName(summaryResponse.data.bankName);
      }
      if (summaryResponse.data.debitAccountNo) {
        setDebitAccountNo(summaryResponse.data.debitAccountNo);
      }

      onSummaryLoaded?.(summaryResponse.data); // NEW
    } else {
      setApiError(t("failed_to_fetch_payment_review_summary"));
    }
  };

  // === PAYROLL DATA FETCH ===================================================
  const fetchPayrollData = async () => {
    const summaryResponse = await gxPayrollReviewTransactionViewSummaryApi({
      gxJobProcessId: gxJobProcessid,
    });

    if (summaryResponse.status === 1 && summaryResponse.data) {
      setPayrollReviewSummary(summaryResponse.data);

      const transactions =
        summaryResponse.data.wpsBulkFileTransactionsHDREntity
          ?.gxwpsBulkFileTransactions || [];
      setAllPayrollTransactions(transactions);

      if (summaryResponse.data.logoName) {
        setLogoName(summaryResponse.data.logoName);
      }
      if (summaryResponse.data.logoPath) {
        setLogoPath(summaryResponse.data.logoPath);
      }
      if (summaryResponse.data.bankName) {
        setBankName(summaryResponse.data.bankName);
      }
      if (summaryResponse.data.debitAccountNo) {
        setDebitAccountNo(summaryResponse.data.debitAccountNo);
      }
      onSummaryLoaded?.(summaryResponse.data); // NEW ==========
    } else {
      setApiError("Failed to fetch payroll review summary");
    }
  };

  // === LAZY TAB DATA FETCH ==================================================
  const fetchTabData = async (tab: string) => {
    if (tab === "all") {
      if (isPayrollFlow) {
        const allTxns =
          payrollReviewSummary?.wpsBulkFileTransactionsHDREntity
            ?.gxwpsBulkFileTransactions || [];
        setAllPayrollTransactions(allTxns);
      } else {
        setAllTransactions(reviewSummary?.gxPayBulkFileTransaction || []);
      }
      return;
    }

    if (isPayrollFlow) {
      const payrollTypeMap: Record<string, string> = {
        valid: "7507",
        errors: "7508",
      };
      const type = payrollTypeMap[tab];
      if (!type) return;

      try {
        const response = await gxPayrollBulkTransactionByStatusApi({
          gxJobProcessId: gxJobProcessid,
          type,
        });
        if (response.status === 1 && response.data) {
          const txns =
            response.data.wpsBulkFileTransactionsHDREntity
              ?.gxwpsBulkFileTransactions || [];
          if (tab === "valid") setValidPayrollTransactions(txns);
          if (tab === "errors") setInvalidPayrollTransactions(txns);
        }
      } catch {}
    } else {
      const paymentTypeMap: Record<string, string> = {
        valid: "4507",
        errors: "4508",
        pending: "4506",
      };
      const type = paymentTypeMap[tab];
      if (!type) return;

      try {
        const response = await gxPaymentsBulkReviewTransactionByStatusApi({
          gxJobProcessId: String(gxJobProcessid),
          type,
        });
        if (response.status === 1 && response.data) {
          const txns = response.data.gxPayBulkFileTransaction || [];
          if (tab === "valid") setValidTransactions(txns);
          if (tab === "errors") setInvalidTransactions(txns);
          if (tab === "pending") setPendingTransactions(txns);
        }
      } catch {}
    }
  };

  // === useEffect ==================================================
  useEffect(() => {
    if (isApprovalMode && (isInProcess || isProcessed)) {
      setHideTabs(true);
      return;
    }

    if (
      statusCode === STATUS_CODES.VALIDATION_IN_PROGRESS ||
      statusCode === STATUS_CODES.UPLOAD_SUCCESS
    ) {
      setHideTabs(true);
      return;
    }

    if (statusCode === STATUS_CODES.FUTURE_DATED && isApprovalMode) {
      setHideTabs(true);
      return;
    }

    setHideTabs(false);
  }, [statusCode, isApprovalMode, isInProcess, isProcessed]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchReviewData = async () => {
      if (!gxJobProcessid) {
        setApiError(t("no_job_process_id_provided"));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setApiError(null);

        if (isPayrollFlow) {
          await fetchPayrollData();
        } else {
          await fetchPaymentData();
        }
      } catch {
        setApiError(t("an_error_occurred_while_fetching_data"));
      } finally {
        setLoading(false);
      }
    };

    fetchReviewData();
  }, [gxJobProcessid, isPayrollFlow]);

  if (loading) {
    return (
      <div className="gx-converging-spinner">
        <Loader size="large" type="converging-spinner" />
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="gx-bup-approve-relateable-container">
        <p className="gx-bup-approve-relateable-error gx-p14-b">{apiError}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="gx-bpr-overview-main-sec-dashboard">
        <div className="gx-overview-review-main">
        <div className="gx-overview-column">
          <BulkPaymentReviewOverview
            reviewSummary={reviewSummary}
            payrollReviewSummary={payrollReviewSummary}
            bulkType={transferredType}
            transferredData={transferredData}
            isPayroll={isPayrollFlow}
            shouldShowTransactionSummary={shouldShowTransactionSummary}
            logoName={logoName}
            logoPath={logoPath}
            bankName={bankName}
            debitAccountNo={debitAccountNo}
          />
        </div>

        <div className="gx-repair-transaction-details">
          <BulkPaymentReviewCardList
            allTransactions={allTransactions}
            validTransactions={validTransactions}
            invalidTransactions={invalidTransactions}
            pendingTransactions={pendingTransactions}
            allPayrollTransactions={allPayrollTransactions}
            validPayrollTransactions={validPayrollTransactions}
            invalidPayrollTransactions={invalidPayrollTransactions}
            bulkType={transferredType}
            reviewSummary={reviewSummary}
            payrollReviewSummary={payrollReviewSummary}
            isPayroll={isPayrollFlow}
            shouldShowTabs={shouldShowTabs}
            shouldShowTransactionSummary={shouldShowTransactionSummary}
            fileType={fileFormat}
            tabLoading={cardListTabLoading}
            onTabChange={async (tab: string) => {
              setCardListTabLoading(true);
              await fetchTabData(tab);
              setCardListTabLoading(false);
            }}
          />
        </div>
        </div>

        <div className="gx-payment-timeline-full">
          <BulkUploadsMetroMap
            status={statusCode}
            jobStatusDesc={jobStatusDesc}
            bulkType={transferredType}
            paymentMetromap={reviewSummary?.paymentMetromap}
            payrollMetromap={payrollReviewSummary?.payrollMetromap}
          />
        </div>
      </div>
    </div>
  );
};

export default BulkUploadIndividualDetails;
